import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Loader2, Trash2, Plus, FlaskConical, ArrowRight, ShieldAlert, CheckCircle2 } from 'lucide-react';
import { apiClient } from '@/utils/apiClient';
import { ToolDescriptionEditor } from './ToolDescriptionEditor';
import type {
  DispatchOverrideView,
  DispatchOverrideTargets,
  DispatchDryRunResult,
  DispatchMatchDetail,
  OverrideScope,
} from '@/utils/apiClient';
import { useTranslation } from '@/i18n';

const inputClass =
  'w-full px-3 py-2 rounded-lg border text-sm border-gray-300 dark:border-gray-600 ' +
  'bg-white dark:bg-gray-700 text-gray-900 dark:text-white ' +
  'focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

const primaryBtn =
  'px-4 py-2 text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 ' +
  'dark:bg-blue-500 dark:hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center gap-2';

const SCOPE_NEEDS_LANGUAGE: OverrideScope[] = ['recipe_pattern', 'specialist_addendum'];
const SCOPE_LANGUAGE_REQUIRED: OverrideScope[] = ['recipe_pattern'];

export const PromptCustomizationPanel: React.FC = () => {
  const { t } = useTranslation();
  const d = (t as any).dispatch || {};

  const [targets, setTargets] = useState<DispatchOverrideTargets | null>(null);
  const [overrides, setOverrides] = useState<DispatchOverrideView[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // draft override
  const [scope, setScope] = useState<OverrideScope>('recipe_pattern');
  const [target, setTarget] = useState('');
  const [language, setLanguage] = useState('ru');
  const [payload, setPayload] = useState('');

  // tester
  const [testQuery, setTestQuery] = useState('');
  const [dry, setDry] = useState<DispatchDryRunResult | null>(null);
  const [testing, setTesting] = useState(false);
  const debounceRef = useRef<number | null>(null);

  const isRecipeScope = scope === 'recipe_pattern' || scope === 'recipe_description';
  const targetOptions = isRecipeScope ? targets?.recipes || [] : targets?.specialists || [];
  const shippedDescription =
    scope === 'recipe_description' && target ? targets?.recipe_descriptions?.[target] : undefined;

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [tg, list] = await Promise.all([
        apiClient.adminGetOverrideTargets(),
        apiClient.adminListOverrides(),
      ]);
      setTargets(tg);
      setOverrides(list);
    } catch (err: any) {
      setError(err?.message || d.loadFailed || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [d.loadFailed]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Reset target when scope switches between recipe/specialist domains.
  useEffect(() => {
    setTarget('');
  }, [scope]);

  const draftCandidate = useCallback(() => {
    if (!payload.trim() || !target) return null;
    return {
      scope,
      target,
      payload,
      language: SCOPE_NEEDS_LANGUAGE.includes(scope) ? language : null,
    };
  }, [scope, target, language, payload]);

  // Debounced dry-run whenever the query or the draft changes.
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    const candidate = draftCandidate();
    if (!testQuery.trim() && !candidate) {
      setDry(null);
      return;
    }
    debounceRef.current = window.setTimeout(async () => {
      setTesting(true);
      try {
        const res = await apiClient.adminDryRunDispatch({
          query: testQuery,
          language: language || 'ru',
          candidate,
        });
        setDry(res);
      } catch (err: any) {
        setError(err?.message || d.actionFailed || 'Action failed');
      } finally {
        setTesting(false);
      }
    }, 350);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [testQuery, scope, target, language, payload, draftCandidate, d.actionFailed]);

  const validation = dry?.validation;
  const canSave =
    !!target &&
    !!payload.trim() &&
    (!SCOPE_LANGUAGE_REQUIRED.includes(scope) || !!language) &&
    // when a candidate was dry-run-validated, require it to pass
    (validation ? validation.ok : true);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      await apiClient.adminCreateOverride({
        scope,
        target,
        payload,
        language: SCOPE_NEEDS_LANGUAGE.includes(scope) ? language : null,
      });
      setSuccess(d.created || 'Saved');
      setPayload('');
      setDry(null);
      await fetchAll();
    } catch (err: any) {
      setError(reasonLabel(err?.message) || d.actionFailed || 'Action failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm(d.confirmDelete || 'Delete this override?')) return;
    setError(null);
    try {
      await apiClient.adminDeleteOverride(id);
      setSuccess(d.deleted || 'Deleted');
      await fetchAll();
    } catch (err: any) {
      setError(err?.message || d.actionFailed || 'Action failed');
    }
  };

  // Map a machine reason code to a friendly localized string.
  const reasonLabel = (reason?: string): string | undefined => {
    if (!reason) return undefined;
    if (reason.startsWith('steals:')) return `${d.reasonSteals || 'Would steal a query'}: "${reason.slice(7)}"`;
    const map: Record<string, string> = {
      regex_error: d.reasonRegexError,
      too_broad: d.reasonTooBroad,
      matches_empty: d.reasonMatchesEmpty,
      secret_detected: d.reasonSecret,
      too_long: d.reasonTooLong,
      unknown_target: d.reasonUnknownTarget,
      bad_language: d.reasonBadLanguage,
    };
    return map[reason] || reason;
  };

  const scopeLabel = (s: string) =>
    s === 'recipe_pattern' ? d.scopeRecipePattern
    : s === 'recipe_description' ? d.scopeRecipeDescription
    : d.scopeSpecialistAddendum;

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{d.title || 'Prompt Customization'}</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">{d.subtitle}</p>
      </div>

      {error && (
        <div className="px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}
      {success && (
        <div className="px-4 py-3 rounded-lg bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-300">
          {success}
        </div>
      )}

      {/* ── Draft editor ─────────────────────────────────────────── */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{d.scope}</label>
            <select className={inputClass} value={scope} onChange={(e) => setScope(e.target.value as OverrideScope)}>
              <option value="recipe_pattern">{d.scopeRecipePattern}</option>
              <option value="recipe_description">{d.scopeRecipeDescription}</option>
              <option value="specialist_addendum">{d.scopeSpecialistAddendum}</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
              {isRecipeScope ? d.targetRecipe : d.targetSpecialist}
            </label>
            <select className={inputClass} value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">—</option>
              {targetOptions.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>
          {SCOPE_NEEDS_LANGUAGE.includes(scope) && (
            <div>
              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{d.language}</label>
              <select className={inputClass} value={language} onChange={(e) => setLanguage(e.target.value)}>
                <option value="ru">ru</option>
                <option value="en">en</option>
              </select>
            </div>
          )}
        </div>

        {/* shipped-default vs override, for the description scope */}
        {shippedDescription !== undefined && (
          <div className="text-xs rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 px-3 py-2">
            <span className="font-medium text-gray-500 dark:text-gray-400">{d.builtin}: </span>
            <span className="text-gray-700 dark:text-gray-300">{shippedDescription}</span>
          </div>
        )}

        <div>
          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">{d.payload}</label>
          <textarea
            className={inputClass + ' font-mono min-h-[70px]'}
            value={payload}
            onChange={(e) => setPayload(e.target.value)}
            placeholder={
              scope === 'recipe_pattern' ? d.payloadPatternHint
              : scope === 'recipe_description' ? d.payloadDescriptionHint
              : d.payloadAddendumHint
            }
          />
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{d.draftHint}</p>
        </div>

        {/* live validation verdict */}
        {validation && (
          <div
            className={
              'flex items-start gap-2 px-3 py-2 rounded-lg text-sm border ' +
              (validation.ok
                ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300'
                : 'bg-amber-50 dark:bg-amber-900/30 border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300')
            }
          >
            {validation.ok ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" /> : <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />}
            <span>{validation.ok ? d.validationValid : `${d.validationInvalid}: ${reasonLabel(validation.reason)}`}</span>
          </div>
        )}

        <div className="flex justify-end">
          <button className={primaryBtn} onClick={handleSave} disabled={!canSave || saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            {d.create}
          </button>
        </div>
      </section>

      {/* ── Dispatch tester (the feedback loop) ──────────────────── */}
      <section className="rounded-xl border border-gray-200 dark:border-gray-700 p-4 space-y-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-blue-500" />
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{d.testerTitle}</h4>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400">{d.testerSubtitle}</p>
        <input
          className={inputClass}
          value={testQuery}
          onChange={(e) => setTestQuery(e.target.value)}
          placeholder={d.testQueryPlaceholder}
        />

        {testing && (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="h-4 w-4 animate-spin" /> …
          </div>
        )}

        {dry && (testQuery.trim() !== '') && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <MatchCard title={d.before} detail={dry.before_detail} d={d} />
            {dry.after_detail && (
              <div className="relative">
                <ArrowRight className="hidden sm:block absolute -left-3 top-6 h-4 w-4 text-gray-300 dark:text-gray-600" />
                <MatchCard title={d.after} detail={dry.after_detail} d={d} highlight />
              </div>
            )}
          </div>
        )}
      </section>

      {/* ── MCP per-tool descriptions (STORY-229) ────────────────── */}
      <ToolDescriptionEditor />

      {/* ── Existing overrides ───────────────────────────────────── */}
      <section>
        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">{d.existingOverrides}</h4>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 text-blue-500 animate-spin" />
          </div>
        ) : overrides.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">{d.noOverrides}</div>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
                <tr>
                  <th className="px-4 py-2 text-left font-medium">{d.scope}</th>
                  <th className="px-4 py-2 text-left font-medium">{d.target}</th>
                  <th className="px-4 py-2 text-left font-medium">{d.language}</th>
                  <th className="px-4 py-2 text-left font-medium">{d.payload}</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {overrides.map((o) => (
                  <tr key={o.id} className="bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/50">
                    <td className="px-4 py-2 whitespace-nowrap text-gray-700 dark:text-gray-300">{scopeLabel(o.scope)}</td>
                    <td className="px-4 py-2 whitespace-nowrap font-medium text-gray-900 dark:text-white">{o.target}</td>
                    <td className="px-4 py-2 text-gray-500">{o.language || '—'}</td>
                    <td className="px-4 py-2 font-mono text-xs text-gray-600 dark:text-gray-400 max-w-[320px] truncate" title={o.payload}>{o.payload}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleDelete(o.id)}
                        className="p-2 rounded-md text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors"
                        title={d.delete}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
};

const MatchCard: React.FC<{ title: string; detail: DispatchMatchDetail; d: any; highlight?: boolean }> = ({
  title,
  detail,
  d,
  highlight,
}) => (
  <div
    className={
      'rounded-lg border p-3 space-y-1.5 ' +
      (highlight
        ? 'border-blue-300 dark:border-blue-700 bg-blue-50/50 dark:bg-blue-900/20'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900')
    }
  >
    <div className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</div>
    {detail.recipe ? (
      <>
        <div className="flex items-center gap-2">
          <span className="font-semibold text-gray-900 dark:text-white">{detail.recipe}</span>
          {detail.is_custom ? (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300">
              {d.custom}
            </span>
          ) : (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400">
              {d.builtin}
            </span>
          )}
        </div>
        {detail.pattern && (
          <div className="text-xs text-gray-500 dark:text-gray-400">
            <span className="text-gray-400">{d.matchedPattern}: </span>
            <code className="font-mono">{detail.pattern}</code>
          </div>
        )}
        {detail.specificity != null && (
          <div className="text-xs text-gray-400">
            {d.specificity}: {detail.specificity} · {d.tier}: {detail.tier}
          </div>
        )}
      </>
    ) : (
      <>
        <div className="text-sm text-gray-500 dark:text-gray-400">{d.noMatch}</div>
        {detail.blocked_by_guard?.length > 0 && (
          <div className="text-xs text-gray-400">
            {d.blockedByGuard}: {detail.blocked_by_guard.join(', ')}
          </div>
        )}
      </>
    )}
  </div>
);
